
import { useEffect, useRef, useState } from 'react';

export default function Standards() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const standards = [
    { icon: 'ri-medal-line', title: 'Quality First', desc: 'We never compromise on materials or execution' },
    { icon: 'ri-focus-3-line', title: 'Detail Obsessed', desc: 'Every millimeter matters in our work' },
    { icon: 'ri-time-line', title: 'Timely Delivery', desc: 'Excellence delivered on schedule' }
  ];

  return (
    <section
      ref={sectionRef}
      className="relative w-full min-h-screen flex items-center justify-center py-16 md:py-24 lg:py-32 overflow-hidden"
    >
      {/* Video Background */}
      <div className="absolute inset-0">
        <video
          autoPlay
          muted
          loop
          playsInline
          className="w-full h-full object-cover"
        >
          <source src="https://videos.pexels.com/video-files/5974401/5974401-uhd_2560_1440_25fps.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-[#0A0A0A]/85" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-[1000px] mx-auto px-4 sm:px-6 md:px-8 text-center">
        <div className={`transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          <div className="flex items-center justify-center gap-3 md:gap-4 mb-6 md:mb-8">
            <div className="w-10 md:w-16 h-px bg-[#C9A86C]" />
            <span className="text-[#C9A86C] text-xs md:text-sm tracking-[0.2em] md:tracking-[0.3em] uppercase">Our Standard</span>
            <div className="w-10 md:w-16 h-px bg-[#C9A86C]" />
          </div>
          
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-serif text-white mb-8 md:mb-12 leading-tight">
            Excellence is
            <span className="block text-[#C9A86C]">Non-Negotiable</span>
          </h2>
        </div>

        <div className={`space-y-4 md:space-y-8 mb-10 md:mb-16 transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          <p className="text-base md:text-xl text-white/70 leading-relaxed">
            We work with clients who value quality over speed, detail over noise, and results over promises.
          </p>
          <p className="text-base md:text-xl text-white/70 leading-relaxed">
            Every project receives the same level of attention, regardless of scale.
          </p>
          <p className="text-lg md:text-2xl text-[#C9A86C] font-serif">
            We do fewer projects—by design.
          </p>
        </div>

        {/* Standards Grid */}
        <div className={`grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-8 transition-all duration-1000 delay-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          {standards.map((item, index) => (
            <div key={index} className="group p-6 md:p-8 border border-white/10 hover:border-[#C9A86C]/50 transition-all duration-500 cursor-pointer">
              <div className="w-12 h-12 md:w-16 md:h-16 mx-auto mb-4 md:mb-6 border border-[#C9A86C] flex items-center justify-center group-hover:bg-[#C9A86C] transition-colors duration-500">
                <i className={`${item.icon} text-xl md:text-2xl text-[#C9A86C] group-hover:text-[#0A0A0A] transition-colors duration-500`} />
              </div>
              <h4 className="text-lg md:text-xl font-serif text-white mb-2 md:mb-3">{item.title}</h4>
              <p className="text-white/50 text-xs md:text-sm">{item.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Decorative Elements - Hidden on mobile */}
      <div className="absolute top-10 md:top-20 left-4 md:left-20 w-16 md:w-32 h-16 md:h-32 border border-[#C9A86C]/20 hidden sm:block" />
      <div className="absolute bottom-10 md:bottom-20 right-4 md:right-20 w-12 md:w-24 h-12 md:h-24 border border-[#C9A86C]/20 hidden sm:block" />
    </section>
  );
}
